import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EffectRef,
  InputSignal,
  model,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { EmployeeRoleModel, UserCustomerProcurationModel } from '@libs/account/models';
import { ButtonComponent } from '@libs/common/components';
import { SelectionListComponent, TextComponent } from '@libs/form/components';
import { CheckboxesInputModel, SelectionListValue, TextInputModel } from '@libs/form/models';
import { CheckboxesOptionModel } from '@libs/shared/models';

interface EmployeeInviteFormValue {
  email: string;
  assignableRoles: SelectionListValue;
}

@Component({
  selector: 'ui-employee-invite',
  templateUrl: './employee-invite.component.html',
  styleUrls: ['./employee-invite.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TextComponent, SelectionListComponent, ReactiveFormsModule, ButtonComponent],
})
export class EmployeeInviteComponent {
  private readonly syncTextWithInput: EffectRef = effect(() => {
    const customer: UserCustomerProcurationModel | undefined = this.customer();
    const id: string | undefined = customer?.customerNumber ?? '';
    const alias: string | undefined = customer?.alias ?? '';

    this.customerNumberControl.setValue(
      alias ? `${id} - ${alias}` : id,
    );
  });

  public customerNumberData: TextInputModel = {
    type: 'text',
    name: 'customerNumber',
    label: 'Kundennummer',
    attributes: {
      disabled: true,
    },
  };

  public emailData: TextInputModel = {
    type: 'email',
    name: 'email',
    label: 'E-Mail',
    hint: '* Pflichtfeld',
    attributes: {
      required: true,
      autocomplete: 'off',
    },
  };

  public readonly customer: InputSignal<UserCustomerProcurationModel | undefined> = model<UserCustomerProcurationModel | undefined>();
  public readonly roles: InputSignal<EmployeeRoleModel[] | undefined> = model<EmployeeRoleModel[] | undefined>();

  public readonly rolesData: Signal<CheckboxesInputModel> = computed(() => ({
    type: 'checkboxes',
    name: 'roles',
    label: 'Berechtigungen',
    options: (this.roles() ?? []).map((role: EmployeeRoleModel): CheckboxesOptionModel => ({
      name: String(role.id),
      label: role.displayName,
    })),
  }));

  public readonly submitResult: OutputEmitterRef<EmployeeInviteFormValue> = output<EmployeeInviteFormValue>();
  public readonly cancelClicked: OutputEmitterRef<void> = output<void>();

  public customerNumberControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });
  public emailControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });

  public rolesControl: FormControl<SelectionListValue> = new FormControl<SelectionListValue>({});

  public onSubmit(event?: Event): void {
    event?.preventDefault();

    if (this.emailControl.invalid) {
      this.emailControl.markAsTouched();

      return;
    }

    this.submitResult.emit({
      email: this.emailControl.getRawValue(),
      assignableRoles: this.rolesControl.value,
    });
  }

  public onCancel(): void {
    this.cancelClicked.emit();
  }
}
